import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@televet/kibble-ui/Button';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { useCallback, useMemo, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { PageContainer } from '@/components/PageContainer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { toaster } from '@/components/ui/toaster';
import {
  EXECUTION_MODE_CONFIG,
  formatDuration,
  namespacePalette,
} from '../-lib/executionConstants';
import { clearStoredFilters } from '@/features/listing-filters/filterStorage';
import { useListingFilters } from '@/features/listing-filters/useListingFilters';
import { useDebouncedFilterText } from '@/features/listing-filters/useDebouncedFilterText';
import {
  calendarDateToIsoEnd,
  calendarDateToIsoStart,
} from '@/features/listing-filters/calendarDate';
import { ExecutionFilters } from './-components/ExecutionFilters';
import { ChainChunks } from './-components/ChainChunks';
import { ChainStatusTags } from './-components/ChainStatusTags';
import {
  chainRefreshInterval,
  chunkListRefreshInterval,
  executionDurationLabel,
} from './-lib/chainDisplay';
import { EXECUTION_MODE_TAG_VARIANT } from './-lib/records';
import { ExecutionStatusTag } from '../-components/StatusTag';
import {
  executionsFiltersSchema,
  executionsSearchSchema,
  type ExecutionsFilterValues,
} from './-lib/filters';

export const Route = createFileRoute('/_authenticated/emitters/executions/')({
  component: EmitterExecutions,
  validateSearch: executionsSearchSchema,
});

const PAGE_SIZE = 50;
const COLUMN_COUNT = 11;
const FILTERS_PAGE = 'emitter-executions';

function EmitterExecutions() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const routeNavigate = Route.useNavigate();
  const filters = Route.useSearch();

  // Cursor-based pagination. Cursors stay in component state rather than the
  // URL: one is only valid for the filters it was issued under.
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack[cursorStack.length - 1] as string | undefined;
  const [expandedChains, setExpandedChains] = useState<ReadonlySet<string>>(new Set());

  // Any change to the URL filters, from a control or a link, starts again at page 1 with nothing
  // expanded. Reset during render so the stale cursor never reaches a query.
  const filtersKey = JSON.stringify(filters);
  const [pagedFiltersKey, setPagedFiltersKey] = useState(filtersKey);
  if (pagedFiltersKey !== filtersKey) {
    setPagedFiltersKey(filtersKey);
    setCursorStack([]);
    setExpandedChains(new Set());
  }

  const [searchDraft, setSearchDraft] = useDebouncedFilterText({
    value: filters.search,
    onCommit: (next) => {
      routeNavigate({ replace: true, search: (prev) => ({ ...prev, search: next }) });
    },
  });

  const applyFilters = useCallback(
    (next: ExecutionsFilterValues) => {
      routeNavigate({ search: next });
    },
    [routeNavigate]
  );

  // "Clear all" also forgets the saved filters, so leaving a chain view that way restores none.
  const clearSavedFilters = useCallback(() => clearStoredFilters(FILTERS_PAGE), []);

  useListingFilters({
    page: FILTERS_PAGE,
    schema: executionsFiltersSchema,
    filters,
    onRehydrate: applyFilters,
    enabled: !filters.chainRootId,
  });

  // Option lists for the Instance and Template columns of the filter dropdown.
  const { data: instancesData } = useQuery(trpc.emitter.listInstances.queryOptions({ limit: 100 }));
  const { data: templatesData } = useQuery(trpc.emitter.listTemplates.queryOptions());

  const queryInput = useMemo(
    () => ({
      limit: PAGE_SIZE,
      cursor: currentCursor,
      ...(filters.status?.length && { status: filters.status }),
      ...(filters.executionMode?.length && { executionMode: filters.executionMode }),
      ...(filters.dataSourceNamespace?.length && {
        dataSourceNamespace: filters.dataSourceNamespace,
      }),
      ...(filters.emitterId?.length && { emitterId: filters.emitterId }),
      ...(filters.templateId?.length && { templateId: filters.templateId }),
      ...(filters.search && { search: filters.search }),
      ...(filters.hasResults && { hasResults: filters.hasResults }),
      ...(filters.hasNewResults && { hasNewResults: filters.hasNewResults }),
      // A chain filter lists every chunk; otherwise one row per run, so paging counts runs.
      ...(filters.chainRootId
        ? { chainRootId: filters.chainRootId }
        : { topLevelOnly: true as const }),
      startDate: calendarDateToIsoStart(filters.startDate),
      endDate: calendarDateToIsoEnd(filters.endDate),
    }),
    [currentCursor, filters]
  );

  const { data, isLoading, isError, error, isPlaceholderData } = useQuery(
    trpc.emitter.listExecutions.queryOptions(queryInput, {
      placeholderData: keepPreviousData,
      // Keeps a running chain's totals and status current, which also ends its chunk list's refresh.
      refetchInterval: ({ state }) => {
        const items = state.data?.items ?? [];
        return filters.chainRootId
          ? chunkListRefreshInterval(items)
          : chainRefreshInterval(items.map((execution) => execution.chain));
      },
    })
  );

  const pageNumber = cursorStack.length + 1;

  // While the previous page stands in, its nextCursor belongs to that page, not this one.
  const nextCursor = isPlaceholderData ? undefined : data?.nextCursor;

  const goNextPage = useCallback(() => {
    if (nextCursor) {
      setCursorStack((prev) => [...prev, nextCursor]);
    }
  }, [nextCursor]);

  const goPrevPage = useCallback(() => {
    setCursorStack((prev) => prev.slice(0, -1));
  }, []);

  const toggleChain = useCallback((chainRootId: string) => {
    setExpandedChains((prev) => {
      const next = new Set(prev);
      if (!next.delete(chainRootId)) next.add(chainRootId);
      return next;
    });
  }, []);

  const { mutate: processExecution, isPending: isProcessing } = useMutation(
    trpc.emitter.processExecution.mutationOptions({
      onSuccess: (result) => {
        if (result.status === 'Completed') {
          toaster.success({
            title: `Execution completed: ${result.totalResults} results${result.durationMs != null ? ` in ${formatDuration(result.durationMs)}` : ''}`,
          });
        } else {
          toaster.error({
            title: 'Execution failed',
            description: result.errorMessage ?? 'Unknown error',
          });
        }
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'listExecutions']],
        });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to process execution',
          description: error.message,
        });
      },
    })
  );

  const { mutate: retryExecution, isPending: isRetrying } = useMutation(
    trpc.emitter.retryExecution.mutationOptions({
      onSuccess: () => {
        toaster.success({ title: 'Retry queued -- new execution created' });
        queryClient.invalidateQueries({
          queryKey: [['emitter', 'listExecutions']],
        });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to retry execution',
          description: error.message,
        });
      },
    })
  );

  // Client-side search filter on top of server-side filters
  return (
    <PageContainer>
      <Flex direction="column" gap={3} width="100%">
        <Breadcrumbs items={[{ label: 'Executions' }]} />

        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
            Execution History
          </Text>
        </Flex>

        <ExecutionFilters
          values={filters}
          onChange={applyFilters}
          instances={instancesData?.items ?? []}
          templates={templatesData ?? []}
          searchDraft={searchDraft}
          onSearchDraftChange={setSearchDraft}
          onClearAll={clearSavedFilters}
        />

        {/* Loading state */}
        {isLoading && (
          <Flex justify="center" py={8}>
            <Text fontSize="sm" variant="subtle">
              Loading executions...
            </Text>
          </Flex>
        )}

        {/* Error state */}
        {isError && (
          <Flex justify="center" py={8}>
            <Text fontSize="sm" variant="danger">
              Failed to load executions: {error?.message ?? 'Unknown error'}
            </Text>
          </Flex>
        )}

        {/* Table */}
        {!isLoading && !isError && (
          <>
            <Table.Root size="sm" width="100%" variant="outline">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader width="40px" />
                  <Table.ColumnHeader>Instance</Table.ColumnHeader>
                  <Table.ColumnHeader>Template</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Version</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Status</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Mode</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Namespace</Table.ColumnHeader>
                  <Table.ColumnHeader>Results</Table.ColumnHeader>
                  <Table.ColumnHeader>Duration</Table.ColumnHeader>
                  <Table.ColumnHeader>Scheduled</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data?.items.map((execution) => {
                  const modeConfig =
                    EXECUTION_MODE_CONFIG[execution.executionMode] ?? EXECUTION_MODE_CONFIG.DryRun;
                  const { chain } = execution;
                  const isExpanded = expandedChains.has(execution.id);

                  return [
                    <Table.Row
                      key={execution.id}
                      cursor="pointer"
                      _hover={{ bg: 'background.subtle' }}
                      onClick={() =>
                        navigate({
                          to: `/emitters/executions/${execution.id}`,
                        })
                      }
                    >
                      <Table.Cell onClick={(e) => e.stopPropagation()}>
                        {chain && (
                          <Button
                            size="xs"
                            variant="ghostNeutral"
                            iconName={isExpanded ? 'chevronDown' : 'chevronRight'}
                            aria-label={isExpanded ? 'Hide chunks' : 'Show chunks'}
                            onClick={() => toggleChain(execution.id)}
                            data-testid={`execution-chain-toggle-${execution.id}`}
                          />
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm" fontWeight="medium">
                          {execution.instance.name ?? execution.instance.id.slice(0, 8)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs" variant="subtle">
                          {execution.instance.template.name}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Text fontSize="xs" variant="subtle">
                          v{execution.templateVersion.versionNumber}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {chain ? (
                          <ChainStatusTags
                            executionId={execution.id}
                            status={chain.status}
                            resumed={chain.resumedFromId !== null}
                          />
                        ) : (
                          <ExecutionStatusTag status={execution.status} />
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Tag
                          label={modeConfig.label}
                          size="sm"
                          fontFamily="mono"
                          variant={EXECUTION_MODE_TAG_VARIANT[execution.executionMode] ?? 'success'}
                        />
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {execution.dataSourceNamespace ? (
                          <Tag
                            label={execution.dataSourceNamespace}
                            size="sm"
                            variant={namespacePalette(execution.dataSourceNamespace)}
                          />
                        ) : (
                          <Text fontSize="xs" variant="placeholder">
                            —
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {chain ? (
                          <Text fontSize="xs" variant="subtle">
                            {chain.rowsRead} rows read · {chain.rowsSent} sent in {chain.chunkCount}{' '}
                            chunks
                          </Text>
                        ) : execution.totalResults != null ? (
                          <Text fontSize="xs" variant="subtle">
                            {execution.newResults ?? 0} new / {execution.totalResults} total
                            {execution.dedupedResults
                              ? ` (${execution.dedupedResults} deduped)`
                              : ''}
                          </Text>
                        ) : (
                          <Text fontSize="xs" variant="subtle">
                            -
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs" variant="subtle">
                          {executionDurationLabel(execution, Date.now())}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs" variant="subtle">
                          {execution.scheduledAt
                            ? new Date(execution.scheduledAt).toLocaleString()
                            : '-'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        <Flex gap={0.5} justify="flex-end" onClick={(e) => e.stopPropagation()}>
                          {execution.status === 'Pending' && (
                            <Button
                              size="xs"
                              variant="ghost"
                              iconName="play"
                              onClick={() => processExecution({ id: execution.id })}
                              disabled={isProcessing}
                              aria-label="Process now"
                              title="Process now"
                            />
                          )}
                          {execution.status === 'Failed' && (
                            <Button
                              size="xs"
                              variant="ghost"
                              iconName="arrowCircle"
                              onClick={() => retryExecution({ id: execution.id })}
                              disabled={isRetrying}
                              aria-label="Retry execution"
                              title="Retry execution"
                            />
                          )}
                        </Flex>
                      </Table.Cell>
                    </Table.Row>,
                    chain && isExpanded && (
                      <Table.Row key={`${execution.id}-chunks`} bg="background.subtle">
                        <Table.Cell colSpan={COLUMN_COUNT} paddingInline={6}>
                          <ChainChunks
                            chainRootId={execution.id}
                            status={chain.status}
                            chunkCount={chain.chunkCount}
                            rowsRead={chain.rowsRead}
                            rowsSent={chain.rowsSent}
                            onOpenAsList={() => setSearchDraft('')}
                          />
                        </Table.Cell>
                      </Table.Row>
                    ),
                  ];
                })}
              </Table.Body>
            </Table.Root>

            {/* Pagination */}
            <Flex justify="space-between" align="center">
              <Text fontSize="xs" variant="subtle">
                {data?.items.length ?? 0} {filters.chainRootId ? 'chunks' : 'runs'} on this page
              </Text>
              <Flex align="center" gap={2}>
                <Text fontSize="sm" variant="subtle">
                  Page {pageNumber}
                </Text>
                <Button
                  size="xs"
                  variant="ghostNeutral"
                  iconName="chevronLeft"
                  onClick={goPrevPage}
                  disabled={cursorStack.length === 0}
                  aria-label="Previous page"
                />
                <Button
                  size="xs"
                  variant="ghostNeutral"
                  iconName="chevronRight"
                  onClick={goNextPage}
                  disabled={!nextCursor}
                  aria-label="Next page"
                />
              </Flex>
            </Flex>

            {data?.items.length === 0 && (
              <Flex justify="center" py={6}>
                <Text fontSize="sm" variant="subtle">
                  No executions found
                </Text>
              </Flex>
            )}
          </>
        )}
      </Flex>
    </PageContainer>
  );
}
